import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { format, getTimezoneOffset, toDate, utcToZonedTime } from 'date-fns-tz';

import { User } from '../../services/data/users/user.class';
import { Space } from '../../services/data/spaces/space.class';
import { ApplicationService } from '../../services/app.service';
import { MediaService } from 'src/app/services/media.service';
import { MapObject } from 'src/app/shared/utilities/types.utilities';
import { BaseComponent } from '../../shared/base.component';
import { InputsModalComponent } from 'src/app/overlays/inputs-modal/inputs-modal.component';
import { AudioVideoModalComponent } from 'src/app/overlays/audio-video-modal/audio-video-modal.component';

import * as dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import { ConfirmModalComponent } from 'src/app/overlays/confirm-modal/confirm-modal.component';
import { addMilliseconds, isAfter, isBefore, parse } from 'date-fns';
import { ChatService } from 'src/app/services/chat/chat.service';
dayjs.extend(customParseFormat);

// declare let grecaptcha: any;
@Component({
    selector: 'app-welcome',
    templateUrl: './welcome.component.html',
    styleUrls: ['./welcome.component.scss'],
})
export class WelcomeComponent extends BaseComponent implements OnInit {
    /** Role dropdown options */
    public role_options: any[] = [
        { id: 'av-test', name: 'testing audio & video settings only' },
        { id: 'patient', name: 'a patient, client or consumer' },
        { id: 'carer', name: 'a relative, carer or friend of patient' },
        { id: 'another provider', name: 'another provider' },
        { id: 'employee', name: 'a NSW Health employee' },
    ];
    /** Role chosen */
    public role: any;
    /** Form group */
    public form: FormGroup = new FormGroup({
        name: new FormControl('', Validators.required),
        phone: new FormControl(),
        role: new FormControl('', Validators.required),
    });
    /** Active waiting room */
    public waiting_room: Space;
    /** Type of error that has occurerd */
    public error: string;
    /** Loading state of the page */
    public loading: string;
    /** Current day of the week */
    public day_of_week: string;
    /** List of opening hours for the active room */
    public hours: { day: string; open: string; close: string }[] = [];
    /** Meeting to add user to */
    public meeting_id: string;
    /** Pin of meeting to add user to */
    public meeting_pin: string;
    /** Last AV test results */
    public test = { audio_tested: '', video_tested: '' };
    /** Whether it is connecting the meeting */
    public is_connecting: boolean;
    /** Staff id of the clinician */
    public staff_id: string;

    constructor(
        private service: ApplicationService,
        private media: MediaService,
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private _chat: ChatService
    ) {
        super();
    }

    public get is_uat(): boolean {
        return this.service.setting('env') === 'staging';
    }

    /** Opening hours for the active space */
    public get opening_hours(): { day: string; open: string; close: string }[] {
        if (this.hours.length <= 0 && this.waiting_room && this.waiting_room.opening_hours) {
            const hours = [];
            for (const day in this.waiting_room.opening_hours) {
                if (this.waiting_room.opening_hours[day]) {
                    let { open, close, available } = this.waiting_room.opening_hours[day];
                    if (!available) {
                        open = 'Closed';
                        close = '';
                    }
                    hours.push({ day, open, close });
                }
            }
            this.hours = hours;
            return hours;
        }
        return this.hours;
    }

    /** Welcome video for the active space */
    public get welcome_video(): string {
        if (this.waiting_room && this.waiting_room.welcome_video) {
            return `https://www.youtube.com/watch?v=${this.waiting_room.welcome_video}`;
        }
        return '';
    }

    public ngOnInit(): void {
        if (!this.service.is_ready) {
            return this.timeout('init', () => this.ngOnInit());
        }
        sessionStorage.clear();
        this.subscription(
            'route.param',
            this.route.paramMap.subscribe((params) => {
                if (params.has('id')) {
                    const room_id = params.get('id').toLowerCase();
                    this.service.Spaces.getRoom(room_id)
                        .then((room: Space) => {
                            this.error = '';
                            this.waiting_room = room;
                        })
                        .catch((err) => {
                            // window.location.href = 'https://www.ehealth.nsw.gov.au/mvc';
                            this.error = 'room';
                        });
                } else {
                    this.error = 'room';
                }
            })
        );
        this.subscription(
            'route.query',
            this.route.queryParamMap.subscribe((params) => {
                if (params.has('name')) {
                    this.form.patchValue({ name: params.get('name') });
                }
                if (params.has('phone')) {
                    this.form.patchValue({ phone: params.get('phone') });
                }
                if (params.has('role')) {
                    this.role = this.role_options.find((role) => role.id === params.get('role'));
                    this.form.patchValue({ role: this.role });
                }
                if (params.has('meeting_id')) {
                    this.meeting_id = params.get('meeting_id');
                }
                if (params.has('meeting_pin')) {
                    this.meeting_pin = params.get('meeting_pin');
                }
                if (params.has('staff_id')) {
                    this.staff_id = params.get('staff_id');
                }
            })
        );
        this.day_of_week = dayjs().format('dddd').toLowerCase();
    }

    /** Check whether room is closed */
    public checkOpeningHours(): boolean {
        if (this.waiting_room?.open_24_7) return;
        const now = new Date();
        const day = format(now, 'EEEE', { timeZone: 'Australia/Sydney' }).toLowerCase();
        const hours = this.waiting_room?.opening_hours[day] || {  open: '9:00 am', close: '5:00 pm', available: true };
        const tz_offset = now.getTimezoneOffset() * 60 * 1000;
        const offset = getTimezoneOffset('Australia/Sydney', now);
        const time = addMilliseconds(now, offset + tz_offset);
        const opening = parse(hours.open, 'h:mm a', now);
        const closing = parse(hours.close, 'h:mm a', now);

        console.log('Day:', time, format(time, 'h:mm a z'), offset + tz_offset);
        console.log('Opening:', format(opening, 'h:mm a z', { timeZone: 'Australia/Sydney' }));
        console.log('Closing:', format(closing, 'h:mm a z', { timeZone: 'Australia/Sydney' }));
        console.log('', isBefore(time, opening), isAfter(time, closing));
        return (
            !hours?.available ||
            format(time, 'HH:mm') < format(opening, 'HH:mm')  ||
            format(time, 'HH:mm') > format(closing, 'HH:mm') 
        );
    }

    /**
     * Submit Form
     */
    public async submit() {
        if (this.form.valid && this.waiting_room) {
            const { name, phone, role, email } = this.form.value;
            const user: any = { name: name.trim(), phone, type: role.id, email };
            if (this.meeting_id) {
                user.meeting_id = this.meeting_id;
                user.meeting_pin = this.meeting_pin || '';
            }
            // If testing do not check opening hour
            if (role.id !== 'av-test') {
                if (!this.waiting_room.available) {
                    this.error = 'unavailable';
                    return;
                }
                if (this.checkOpeningHours()) {
                    this.error = 'closed';
                    return;
                }
            }
            // Open audio and video modal
            if (!(this.test.audio_tested && this.test.video_tested) || role.id === 'av-test') {
                await this.testAudioVideo();
            }
            // If testing only, stop here
            if (role.id === 'av-test') {
                this.form.patchValue({ role: '' });
                return;
            }
            try {
                await this.validateUser(user);
                this._chat.setRole(user.type);

                if (this.meeting_id) {
                    const meeting = new User(this.service.Users, {
                        ...user,
                        waiting_room: this.waiting_room.id,
                        welcome_video: this.waiting_room.welcome_video,
                        exit_survey: this.waiting_room.userExitSurvey(user.type),
                        pexip_id: this.meeting_id,
                        pexip_pin: this.meeting_pin,
                    });
                    this.service.set('meeting', meeting);
                    this.service.Users.current = new User(this.service.Users, {
                        ...meeting,
                        ...user,
                    });
                    this.service.navigate([
                        '/',
                        'room',
                        this.waiting_room.short_name || this.waiting_room.id,
                    ]);
                } else {
                    this.loading = 'Retrieving clinicians for room...';
                    const list = await this.service.Users.show(this.waiting_room.short_name);
                    this.loading = null;
                    this.waiting_room = new Space(this.service.Spaces, {
                        ...this.waiting_room,
                        list_member_names: list,
                    });
                    // Connect directly to meeting if staff id is present
                    if (this.staff_id && this.role === 'patient') {
                        this.connectToMeeting({
                            clinician: { id: this.staff_id },
                            appointment_time: dayjs()
                                .minute(Math.ceil(dayjs().minute() / 5) * 5)
                                .unix(),
                        });
                    } else {
                        this.checkAppointment();
                    }
                }
            } catch (e) {
                this.loading = null;
                this.error = e.message;
            }
        } else {
            this.error = 'room';
        }
    }

    /**
     * Test AV / Open modal
     */
    public async testAudioVideo(): Promise<void> {
        return new Promise((resolve, reject) => {
            const ref = this.dialog.open(AudioVideoModalComponent, {
                minWidth: '35vw',
                disableClose: true,
            });
            this.subscription(
                'audio_video_modal',
                ref.componentInstance.event.subscribe((event) => {
                    if (event.reason === 'done') {
                        const { audio_tested, video_tested, audio_source, video_source } =
                            event.metadata;
                        this.test.video_tested = video_tested;
                        this.test.audio_tested = audio_tested;
                        this.service.Users.setAudioVideoResults(video_tested, audio_tested);
                        this.media.saveMedia(audio_source, video_source);
                    }
                })
            );
            ref.afterClosed().subscribe(() => resolve());
        });
    }

    /**
     * Check appointment details for the user/patient
     */
    public checkAppointment() {
        const { name, phone, role, email } = this.form.value;
        const { audio_tested, video_tested } = this.test;
        const user = { name, phone, type: role.id, email, audio_tested, video_tested };
        const ref = this.dialog.open(InputsModalComponent, {
            disableClose: true,
            data: {
                user,
                room: this.waiting_room,
            },
        });
        this.subscription(
            'inputs_modal',
            ref.componentInstance.event.subscribe((event) => {
                if (event.reason === 'done') {
                    this.service.Users.current = new User(this.service.Users, { ...user });
                    this.checkBillingConsent({ ...event.metadata, ...this.test });
                }
            })
        );
    }

    /**
     * Check billing consent for patient if applicable
     * @param data Data from previous form
     */
    public checkBillingConsent(data) {
        const { name, phone, role, email } = this.form.value;
        const user = { name, phone, type: role.id, email };
        if (
            this.waiting_room &&
            this.waiting_room.billing_consent &&
            user &&
            user.type === 'patient'
        ) {
            const overlay_data = {
                title: 'Billing Consent',
                content: `
                    Please note that by starting this video conference you consent to be treated as a Medicare 
                    Bulk-Billed patient (private patient) and the service will claim Medicare on your behalf.
                `,
                cancel: 'No, I do not accept',
                action: 'I accept',
            };
            const ref = this.dialog.open(ConfirmModalComponent, {
                data: overlay_data,
                width: '23em',
                disableClose: true,
            });
            this.subscription(
                'confirm_modal',
                ref.componentInstance.event.subscribe((event) => {
                    this.connectToMeeting({ ...data, billing_consent: event.reason === 'done' });
                })
            );
        } else {
            this.connectToMeeting(data);
        }
    }

    /**
     * Create meeting and connect
     * @param data Form data to associate with the meeting
     */
    public connectToMeeting(data: any) {
        if (this.is_connecting) {
            return;
        }
        this.is_connecting = true;
        const clinician = data.clinician || {};
        this.service.Users.update(this.waiting_room.short_name, {
            ...data,
            appointment_time: dayjs(data.appointment_time).unix(),
            patient: { name: data.patient_name, id: data.patient_id },
            doctor_id: clinician.id || '',
        }).then(
            (space) => {
                const { name, phone, role, email } = this.form.value;
                const user = { name, phone, type: role.id, email };
                const meeting = new User(this.service.Users, {
                    ...space,
                    ...user,
                    waiting_room: this.waiting_room.id,
                    welcome_video: this.waiting_room.welcome_video,
                    exit_survey: this.waiting_room.userExitSurvey(user.type),
                    pexip_url: this.waiting_room.pexip_url,
                });
                this.service.set('meeting', meeting);
                this.service
                    .navigate(['/', 'room', this.waiting_room.short_name || this.waiting_room.id])
                    .then(() => {
                        this.is_connecting = false;
                    });
            },
            (err) => {
                this.is_connecting = false;
                if (err.status === 406) {
                    this.error = 'closed';
                } else {
                    this.error = 'meeting';
                }
            }
        );
    }

    /**
     * Validate user credentials
     * @param user User details to associate with the session
     */
    public async validateUser(user: MapObject) {
        user.captcha = this.service.get('reCAPTCHA');
        this.loading = 'Establishing user session...';
        await this.service.Users.add(user).catch((e) => {
            this.service.notifyError('Error initialising user session');
            throw new Error('session');
        });
        this.loading = '';
    }
}
